import { LitElement, html, css } from 'lit'
import { api } from '../api.js'
import { toast } from './app-toast.js'

// Connected applications (§20.5) — what an agent has granted access to their worklog,
// and the button that takes it back. Disconnecting revokes every token the
// application holds at once, so it stops working on its next request rather than
// whenever its current token would have expired.
class PageConnections extends LitElement {
  static properties = {
    _grants: { state: true },
    _loading: { state: true }
  }

  static styles = css`
    :host { display: block; font-family: var(--font-sans); color: var(--eggplant-500); }
    h1 { margin: 0 0 6px; font-size: 32px; font-weight: 800; letter-spacing: -0.02em; }
    .sub { color: var(--neutral-600); font-size: 15px; margin-bottom: 28px; max-width: 60ch; }
    .card { background: var(--white); border-radius: var(--radius-card); box-shadow: var(--shadow-card); padding: 24px; }
    .row { display: flex; justify-content: space-between; align-items: center; gap: 16px;
           padding: 16px 0; border-top: 1px solid var(--border-input); }
    .row:first-of-type { border-top: none; }
    .name { font-size: 16px; font-weight: 700; }
    .meta { color: var(--neutral-600); font-size: 13.5px; margin-top: 3px; }
    .disabled { color: #B4232B; font-size: 13.5px; margin-top: 3px; }
    .empty { color: var(--neutral-600); font-size: 15px; }
    button { font: inherit; font-size: 13px; font-weight: 700; cursor: pointer; padding: 0; border: none;
             background: none; color: var(--sky-500); text-decoration: underline; text-underline-offset: 3px; }
    button:hover { color: #B4232B; }
  `

  connectedCallback() {
    super.connectedCallback()
    this._load()
  }

  async _load() {
    this._loading = true
    try {
      const { grants } = await api.get('/oauth/grants')
      this._grants = grants
    } catch (err) {
      toast(err.message || 'Failed to load connections', 'error')
    } finally {
      this._loading = false
    }
  }

  async _revoke(grant) {
    try {
      await api.delete(`/oauth/grants/${encodeURIComponent(grant.clientId)}`)
      toast(`${grant.name} disconnected`, 'success')
      this._load()
    } catch (err) {
      toast(err.message || 'Failed to disconnect', 'error')
    }
  }

  _when(value, never) {
    if (!value) return never
    return new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  }

  render() {
    return html`
      <h1>Connected applications</h1>
      <p class="sub">Applications you have allowed to read your worklog — which tickets you
        worked on, and for how long. They see your own work only, and cannot change anything.</p>
      <div class="card">
        ${this._loading && !this._grants ? html`<div class="empty">Loading…</div>`
          : !this._grants?.length
            ? html`<div class="empty">Nothing is connected. An application will send you here to ask.</div>`
            : this._grants.map(g => html`
              <div class="row">
                <div>
                  <div class="name">${g.name}</div>
                  <div class="meta">Connected ${this._when(g.connectedAt, 'recently')} ·
                    last read ${this._when(g.lastUsedAt, 'never')}</div>
                  ${g.active ? '' : html`<div class="disabled">This application has been disabled by an administrator.</div>`}
                </div>
                <button @click=${() => this._revoke(g)}>Disconnect</button>
              </div>`)}
      </div>`
  }
}

customElements.define('page-connections', PageConnections)
